import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core'
import { AjvError } from '@rjsf/core'
import { LoadingContainer } from 'components/Common/LoadingContainer'
import { LocalizedButton } from 'components/Localization/LocalizedButton'
import { LocalizedTypography } from 'components/Localization/LocalizedTypography'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { WorkingStepsController } from 'controllers/WorkingStepsController'
import { useController } from 'customHooks/useController'
import { newProjectActions } from 'features/BillOfMaterials/store/newProjectReducer'
import _, { isEqual } from 'lodash'
import { FormDataDto } from 'model/FormDataDto'
import {
  AssemblyHeaderRow,
  AssemblyRow,
  BoMItemType,
} from 'model/Project/BoMItemRow'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from 'store/configureStore'
import { useAppDispatch } from 'store/configureStore'
import {
  AdditionalWorkingStepDto,
  RowDto,
  WorkingStepDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import projectImageUrl from 'services/APIs/InternalAPI/ProjectImages'
import { ErrorMessage, ShowException } from 'store/Application/appActions'
import { RootAppState } from 'store/configureStore'
import { useOrganizationAndProjectContext } from 'utils/useOrganizationContext'
import { WorkingStepForm } from '../Form/WorkingStepForm'
import { WorkingStepAutoComplete } from './WorkingStepAutoComplete'
import './WorkingStepsDialog.scss'
import { WorkingStepTable } from './WorkingStepTable'

export type WorkingStepsDialogProps = {
  rowIds: string[]
  isActive: boolean
  onClose?: (isActive?: boolean) => void
  bomItemType: BoMItemType
  activeWorkingStep?: WorkingStepDto
}

const WorkingStepsDialog = (props: WorkingStepsDialogProps) => {
  const { t } = useTranslation('project')
  const { getLocalizedWorkingStep } = useLocalizedWorkingStepLabel()

  const { partyId: organizationId, projectId } =
    useOrganizationAndProjectContext()
  const { controller, loading } = useController(
    () => new WorkingStepsController(organizationId)
  )
  const dispatch = useAppDispatch()

  const [availableWorkingSteps, setAvailableWorkingSteps] = useState<
    WorkingStepDto[]
  >([])

  /**
   * the workingsteps to be removed
   */
  const [removedWorkingSteps, setRemovedWorkingSteps] = useState<
    {
      workingStep: WorkingStepDto
      formData: FormDataDto
      costDriverForm: AdditionalWorkingStepDto
    }[]
  >([])

  /**
   * the working steps included by user
   */
  const [includedWorkingSteps, setIncludedWorkingSteps] = useState<
    {
      workingStep: WorkingStepDto
      formData: FormDataDto
      isEmpty: boolean
      costDriveForm: AdditionalWorkingStepDto
    }[]
  >([])

  const [validationErrors, setValidationError] =
    useState<Record<string, AjvError[]>>(undefined)

  const [currentWorkingStep, setCurrentWorkingStep] = useState<WorkingStepDto>(
    props.activeWorkingStep
  )

  const [currentWorkingStepForm, setCurrentWorkingStepForm] =
    useState(undefined)

  useEffect(() => {
    ;(async () => {
      if (!currentWorkingStep) return

      const includedForm = includedWorkingSteps.find(
        (x) => x.workingStep.key === currentWorkingStep.key
      )

      if (includedForm && includedForm.costDriveForm) {
        setCurrentWorkingStepForm(includedForm.costDriveForm)
      } else {
        const workingStepForm = await controller.GetWorkingStepForm(
          projectId,
          props.rowIds[0],
          currentWorkingStep.primaryWorkingStep
        )
        setCurrentWorkingStepForm(workingStepForm)
      }
    })()
    // eslint-disable-next-line
  }, [currentWorkingStep])

  const { bomItemId, thumbnail3d, thumbnail2d } = useAppSelector(
    (state: RootAppState) => {
      const partyId = state.user.organizationContext.id
      const projectId = state.project.activeProject?.id

      switch (props.bomItemType) {
        case BoMItemType.project: {
          return {
            bomItemId: projectId,
            workingSteps: state.project.activeProject.workingSteps ?? [],
          }
        }
        case BoMItemType.assembly:
        case BoMItemType.assemblyHeader: {
          const assemblyHeader = state.project.flatView[
            props.rowIds[0]
          ] as AssemblyHeaderRow

          if (assemblyHeader) {
            return {
              bomItemId: assemblyHeader.assembly.id,
              workingSteps: assemblyHeader.assembly.assemblyWorkingStepDtos,
              thumbnail3d:
                assemblyHeader.assembly.thumbnail3d &&
                projectImageUrl(
                  partyId,
                  projectId,
                  assemblyHeader.assembly.thumbnail3d
                ),
            }
          }

          const assembly = state.project.treeView[
            props.rowIds[0]
          ] as AssemblyRow

          return {
            bomItemId: assembly.id,
            workingSteps: assembly.workingSteps,
            thumbnail3d:
              assembly.thumbnail3d &&
              projectImageUrl(partyId, projectId, assembly.thumbnail3d),
          }
        }
        case BoMItemType.row: {
          const partType = (state.project.flatView[props.rowIds[0]] ||
            state.project.treeView[props.rowIds[0]]) as RowDto

          return {
            bomItemId: partType.id,
            workingSteps: partType.workingSteps,
            thumbnail3d:
              partType.thumbnail3d &&
              projectImageUrl(partyId, projectId, partType.thumbnail3d),
            thumbnail2d:
              partType.thumbnail2d &&
              projectImageUrl(partyId, projectId, partType.thumbnail2d),
          }
        }
        default:
          return null
      }
    },
    isEqual
  )

  async function loadData() {
    try {
      const data = await controller.GetWorkingSteps(projectId, bomItemId)

      setAvailableWorkingSteps(data.availableWorkingSteps)
      // setSelectedWorkingSteps(data.selectedWorkingSteps)
      setIncludedWorkingSteps(
        data.selectedWorkingSteps.map((x) => ({
          workingStep: x,
          formData: undefined,
          costDriveForm: undefined,
          isEmpty: false,
        }))
      )
      if (currentWorkingStep) {
        if (
          data.availableWorkingSteps.findIndex(
            (x) => x.key === currentWorkingStep.key
          ) === -1 &&
          data.selectedWorkingSteps.findIndex(
            (x) => x.key === currentWorkingStep.key
          ) === -1
        ) {
          setCurrentWorkingStep(undefined)
        }
      }
    } catch (err) {
      ShowException(t('project:working-steps--dialog-title'), err)
    }
  }

  useEffect(() => {
    loadData()

    return () => {
      setAvailableWorkingSteps([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSave = async () => {
    props.onClose(false)
    requestIdleCallback(async () => {
      try {
        await controller.handleSaveAndRemoveWorkingSteps(
          props.rowIds,
          includedWorkingSteps.filter((x) => Boolean(x.formData) || x.isEmpty),
          removedWorkingSteps.map((x) => x.workingStep)
        )
        dispatch(newProjectActions.setBomItemInSearch(props.rowIds[0]))
      } catch (err) {
        ErrorMessage('working steps', (err && err['message']) || err)
      }
    })
  }

  const includeWorkingStepForm = async (formData) => {
    try {
      const includedWorkingStepIndex = includedWorkingSteps.findIndex(
        (x) => x.workingStep.key === currentWorkingStep.key
      )
      if (includedWorkingStepIndex === -1) {
        setIncludedWorkingSteps((state) => [
          ...state,
          {
            workingStep: currentWorkingStep,
            formData,
            isEmpty: Boolean(formData),
            costDriveForm: { ...currentWorkingStepForm, formData: formData },
          },
        ])
        // check if it is in the removed items
        setRemovedWorkingSteps((state) => {
          const filteredRemoved = state.filter(
            (x) => x.workingStep.key !== currentWorkingStep.key
          )
          return filteredRemoved
        })
      } else {
        setIncludedWorkingSteps((state) => {
          const newState = state.map((x) =>
            x.workingStep.key === currentWorkingStep.key
              ? {
                  workingStep: currentWorkingStep,
                  formData,
                  isEmpty: Boolean(formData),
                  costDriveForm: {
                    ...currentWorkingStepForm,
                    formData,
                    isEmpty: Boolean(formData),
                  },
                }
              : x
          )

          return newState
        })
      }

      Object.keys(currentWorkingStepForm.formData || {}).forEach(
        (formDataKey) => {
          // if an item was deleted it should remove all validation errors related to that field if its an array
          if (
            (_.isArray(currentWorkingStepForm.formData[formDataKey]) &&
              currentWorkingStepForm.formData[formDataKey].length >
                formData[formDataKey]?.length) ||
            0
          ) {
            currentWorkingStepForm.formData[formDataKey].forEach(
              (item, index) => {
                if (
                  !_.isEqual(
                    formData[formDataKey][index],
                    currentWorkingStepForm.formData[formDataKey][index]
                  )
                ) {
                  Object.getOwnPropertyNames(item).forEach((prop) => {
                    setValidationError((state) => {
                      const newState = { ...state }
                      if (newState[currentWorkingStep.key]) {
                        delete newState[currentWorkingStep.key][
                          `root_${formDataKey}_${index}_${prop}`
                        ]
                        if (_.isEmpty(newState[currentWorkingStep.key])) {
                          delete newState[currentWorkingStep.key]
                        }
                      }
                      return newState
                    })
                  })
                }
              }
            )
          }
        }
      )

      setCurrentWorkingStepForm((state) => ({ ...state, formData }))

      setAvailableWorkingSteps((state) =>
        state.filter((x) => x.key !== currentWorkingStep.key)
      )
      // setCurrentWorkingStep(undefined)
    } catch (err) {
      ErrorMessage(
        t('project:working-steps--dialog-title', 'working steps'),
        t('common:unexpected-error', {
          defaultValue: 'unexpected error.\n({{error}})',
          replace: {
            error: err['message'],
          },
        })
      )
      loadData()
    }
  }

  const handleDelete = async (workingStep: WorkingStepDto) => {
    try {
      setIncludedWorkingSteps((state) =>
        state.filter((x) => x.workingStep.key !== workingStep.key)
      )

      setRemovedWorkingSteps((state) => [
        ...state.filter((x) => x.workingStep.key !== workingStep.key),
        {
          costDriverForm: undefined,
          formData: undefined,
          workingStep,
        },
      ])

      if (
        currentWorkingStep.primaryWorkingStep === workingStep.primaryWorkingStep
      ) {
        setCurrentWorkingStep(undefined)
      }
    } catch (err) {
      ErrorMessage(
        t('project:working-steps--dialog-title', 'working steps'),
        t('common:unexpected-error', {
          defaultValue: 'unexpected error.\n({{error}})',
          replace: {
            error: err['message'],
          },
        })
      )
    }
  }

  const onValidationError = React.useCallback(
    (errors: AjvError[]) => {
      const errorsWithoutNulls = _.omitBy(errors, _.isNil) as Record<
        string,
        string
      >
      const fixedErrors = _.pickBy(errors, _.isNil) as Record<string, string>

      if (Object.keys(fixedErrors).length) {
        setValidationError((state) => {
          const newState = { ...state }
          Object.keys(fixedErrors).forEach((k) => {
            newState[currentWorkingStep.key] &&
              delete newState[currentWorkingStep.key][k]
          })

          if (
            Object.keys(newState[currentWorkingStep.key] || {}).length === 0
          ) {
            delete newState[currentWorkingStep.key]
          }

          return newState
        })
      } else {
        setValidationError((state) => {
          return Object.assign({}, state, {
            [currentWorkingStep.key]: {
              ...(state || {})[currentWorkingStep.key],
              ...errorsWithoutNulls,
            },
          })
        })
      }
    },
    [currentWorkingStep?.key]
  )

  return (
    <Dialog
      className="working-steps-dialog"
      open={props.isActive}
      onClose={(_, reason) =>
        reason !== 'backdropClick' && props.onClose(false)
      }
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle className="working-steps-dialog-title">
        <LocalizedTypography
          translationKey={'project:additional-working-steps--title'}
          options={{
            defaultValue: '{{bomItemType}} tional working steps',
            replace: {
              bomItemType: `$t(project:${BoMItemType[props.bomItemType]})`,
            },
          }}
          variant="h6"
        >
          additional working steps
        </LocalizedTypography>
        <div className="models">
          {thumbnail3d && (
            <img loading="lazy" src={thumbnail3d} alt="part 3d model" />
          )}
          {thumbnail2d && (
            <img loading="lazy" src={thumbnail2d} alt="part 2d model" />
          )}
        </div>
      </DialogTitle>
      <DialogContent className="additional-working-steps--container">
        <LoadingContainer variant="spinner" loading={!includedWorkingSteps}>
          <>
            <div className="additional-working-steps-list">
              <WorkingStepAutoComplete
                availableWorkingSteps={[
                  ...availableWorkingSteps,
                  ...(removedWorkingSteps.map((x) => x.workingStep) || []),
                ]}
                onOptionSelected={(workingStep) => {
                  setCurrentWorkingStep(workingStep)
                  setIncludedWorkingSteps((state) => [
                    ...state,
                    {
                      workingStep,
                      costDriveForm: undefined,
                      isEmpty: true,
                      formData: undefined,
                    },
                  ])
                  setAvailableWorkingSteps((state) =>
                    state.filter((x) => x.key !== workingStep.key)
                  )
                  setRemovedWorkingSteps((state) =>
                    state.filter((x) => x.workingStep.key !== workingStep.key)
                  )
                }}
              />
              <WorkingStepTable
                setCurrentWorkingStep={setCurrentWorkingStep}
                titleTKey="project:working-steps-included"
                workingSteps={includedWorkingSteps.map((x) => x.workingStep)}
                currentWorkingStep={currentWorkingStep}
                handleDelete={handleDelete}
                loading={loading}
                validationErrors={validationErrors}
              />
              {/* <WorkingStepTable
                setCurrentWorkingStep={setCurrentWorkingStep}
                titleTKey="project:working-steps-available"
                workingSteps={[
                  ...availableWorkingSteps,
                  ...(removedWorkingSteps.map((x) => x.workingStep) || []),
                ]}
                currentWorkingStep={currentWorkingStep}
                handleAddWorkingStep={(workingStep) => {
                  setCurrentWorkingStep(workingStep)
                  setIncludedWorkingSteps((state) => [
                    ...state,
                    {
                      workingStep,
                      costDriveForm: undefined,
                      isEmpty: true,
                      formData: undefined,
                    },
                  ])
                  setAvailableWorkingSteps((state) =>
                    state.filter((x) => x.key !== workingStep.key)
                  )
                  setRemovedWorkingSteps((state) =>
                    state.filter((x) => x.workingStep.key !== workingStep.key)
                  )
                }}
                loading={loading}
                validationErrors={validationErrors}
              /> */}
            </div>
            <div className="working-step-form--container">
              <LoadingContainer
                variant="spinner"
                loading={
                  loading[
                    WorkingStepsController.WorkingStepsOperations.GET_FORM
                  ] //||
                  // loading[WorkingStepsController.WorkingStepsOperations.SAVE_DATA]
                }
                noMessage
                empty={
                  (!currentWorkingStep || !currentWorkingStepForm) && (
                    <LocalizedTypography
                      translationKey={'project:addtional-working-steps-empty'}
                    >
                      select a working step to include or to edit
                    </LocalizedTypography>
                  )
                }
              >
                <>
                  {/* <pre>
                    {
                      JSON.stringify(currentWorkingStepForm, null, 2)
                    }
                  </pre> */}

                  {currentWorkingStep ? (
                    <WorkingStepForm
                      extraErrors={
                        validationErrors &&
                        validationErrors[currentWorkingStep.key]
                      }
                      workingStepForm={{ ...currentWorkingStepForm }}
                      isSaving={
                        loading[
                          WorkingStepsController.WorkingStepsOperations
                            .SAVE_DATA
                        ]
                      }
                      saveWorkingStepForm={includeWorkingStepForm}
                      editing={includedWorkingSteps
                        ?.map((x) => x.workingStep.key)
                        ?.includes(currentWorkingStep?.key)}
                      workingStepName={
                        currentWorkingStep &&
                        getLocalizedWorkingStep(
                          currentWorkingStep.primaryWorkingStep
                        )
                      }
                      setValidationError={onValidationError}
                    />
                  ) : null}
                </>
              </LoadingContainer>
            </div>
          </>
        </LoadingContainer>
      </DialogContent>
      <DialogActions>
        <LocalizedButton
          onClick={() => props.onClose(false)}
          translationKey="common:close"
          variant="outlined"
        />
        <LocalizedButton
          variant="contained"
          color="primary"
          onClick={() => handleSave()}
          disabled={
            Boolean(Object.keys(validationErrors || {}).length) ||
            loading[WorkingStepsController.WorkingStepsOperations.SAVE_DATA] ||
            !includedWorkingSteps.filter(
              (x) => Boolean(x.formData) || x.isEmpty
            )
          }
          translationKey="common:save"
        />
      </DialogActions>
      {/* <pre>{JSON.stringify(validationErrors, null, 2)}</pre> */}
    </Dialog>
  )
}

export default WorkingStepsDialog
